/**
 * WorkoutCard Component
 * Thumbnail card displaying workout with zone profile visualization
 */

import { Favorite, FavoriteBorder, Timer } from '@mui/icons-material';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import type { WorkoutStep } from '@shared/types';
import React from 'react';
import { WorkoutZoneProfile } from './WorkoutZoneProfile';

export interface WorkoutCardData {
  id: string;
  name: string;
  type: 'easy' | 'hard';
  sportType: 'running' | 'cycling';
  total_duration: number;
  TSS: number;
  environment?: string;
  category?: string;
  steps: WorkoutStep[];
}

interface WorkoutCardProps {
  workout: WorkoutCardData;
  onClick: (workout: WorkoutCardData) => void;
  isFavorite?: boolean;
  onToggleFavorite?: (workoutId: string) => Promise<void>;
}

export const WorkoutCard: React.FC<WorkoutCardProps> = ({
  workout,
  onClick,
  isFavorite,
  onToggleFavorite,
}) => {
  const theme = useTheme();

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (onToggleFavorite) {
      await onToggleFavorite(workout.id);
    }
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes}min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.2s, box-shadow 0.2s',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
    >
      {/* Favorite Button */}
      {onToggleFavorite && (
        <IconButton
          onClick={handleFavoriteClick}
          aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            zIndex: 1,
            color: isFavorite ? theme.palette.error.main : theme.palette.action.disabled,
            '&:hover': {
              color: theme.palette.error.main,
              backgroundColor: theme.palette.action.hover,
            },
          }}
          size="small"
        >
          {isFavorite ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
        </IconButton>
      )}
      <CardActionArea
        onClick={() => onClick(workout)}
        aria-label={`View ${workout.name} workout details`}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <CardContent sx={{ flexGrow: 1, pt: 1.5, pb: 1 }}>
          {/* Workout Name */}
          <Typography
            variant="subtitle1"
            component="h3"
            sx={{
              fontWeight: 600,
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              pr: 3, // Space for favorite button
            }}
          >
            {workout.name}
          </Typography>

          {/* Zone Profile Visual */}
          <Box sx={{ mb: 1 }}>
            <WorkoutZoneProfile
              steps={workout.steps}
              height={50}
              sportType={workout.sportType}
              environment={workout.environment}
            />
          </Box>

          {/* Chips Row with Time */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Category Chip */}
            {workout.category && (
              <Chip
                label={workout.category}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.5 } }}
              />
            )}

            {/* Type Chip (Hard/Easy) */}
            <Chip
              label={workout.type === 'hard' ? 'Hard' : 'Easy'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                backgroundColor:
                  workout.type === 'hard' ? theme.palette.error.light : theme.palette.success.light,
                color:
                  workout.type === 'hard'
                    ? theme.palette.error.contrastText
                    : theme.palette.success.contrastText,
                '& .MuiChip-label': { px: 0.5 },
              }}
            />

            {/* TSS Chip */}
            {workout.TSS > 0 && (
              <Chip
                label={`TSS ${workout.TSS}`}
                size="small"
                variant="outlined"
                sx={{ height: 20, fontSize: '0.7rem', '& .MuiChip-label': { px: 0.5 } }}
              />
            )}

            {/* Duration - Right Aligned */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
              <Timer sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {formatDuration(workout.total_duration)}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default WorkoutCard;
